"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { BaseCoverageId } from "@/config/coverages";
import { OfferResponse } from "@/types/simpego-api";
import { buildRows } from "@/utils/coverageRows";
import { isPetYoungerThanAgeBand } from "@/utils/coveragePayload";
import CoverageDetailItem from "./CoverageDetailItem";

interface CartCoverageListProps {
  /** The offer the price was calculated for; `null` while it is still loading. */
  offer: OfferResponse | null;
  coverageSelections: Partial<Record<BaseCoverageId, boolean>>;
  /** Coverages the underwriting rejected: never listed as insured. */
  declinedCoverages?: Partial<Record<BaseCoverageId, string>>;
  petBirthDate?: Date | null;
}

/** The cart's compact list (bold heading, one line per figure, check marks): the wording and values of
 * the comparison table, laid out like the original cart. */
const CartCoverageList: React.FC<CartCoverageListProps> = ({ offer, coverageSelections, declinedCoverages = {}, petBirthDate }) => {
  const { t } = useTranslation();
  const isYoung = isPetYoungerThanAgeBand(petBirthDate ?? null);
  const rows = React.useMemo(() => {
    const selections = { ...coverageSelections };
    for (const id of Object.keys(declinedCoverages) as BaseCoverageId[]) selections[id] = false;
    return buildRows(selections, isYoung);
  }, [coverageSelections, declinedCoverages, isYoung]);

  if (!offer) {
    return <Loader2 className="mx-auto my-4 h-6 w-6 animate-spin text-primary" aria-label={t("common.loading")} />;
  }

  return (
    <div className="p-4">
      {rows.map((row, index) => {
        if (row.kind === "section") {
          return <CoverageDetailItem key={index} label={t(row.labelKey)} isCovered hideIcon isLabelBold />;
        }
        const label = t(row.labelKey);
        if (row.kind === "metric") {
          const metric = row.value(offer, t);
          // The other age band ("ab 9 Jahren 80%") belongs in the same line as the figure.
          const value = metric && (metric.sub ? `${metric.main} (${metric.sub})` : metric.main);
          return <CoverageDetailItem key={index} label={label} value={value} isCovered hideIcon nested={row.nested} />;
        }
        const coverage = row.value(offer, t);
        return <CoverageDetailItem key={index} label={label} value={coverage.detail} isCovered={coverage.isCovered} nested={row.nested} />;
      })}
    </div>
  );
};

export default CartCoverageList;
